import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import expense from "../expense.json";
import GLOBALVAR from "../constants"; // Import GLOBALVAR constant
import ToPeso from "../components/misc/ToPeso";
import { Table } from "react-bootstrap";

const jsonData = expense;

function ExpenseDropdown() {
    const [selectedOption, setSelectedOption] = useState(null);
    const [amount, setAmount] = useState("");
    const [dateAdded, setDateAdded] = useState("");
    const [credit, setCredit] = useState(false); // Default to true for credit
    const [remarks, setRemarks] = useState("");
    const [responseMessage, setResponseMessage] = useState(null);
    const [transactionLogs, setTransactionLogs] = useState([]); // Logs state
    const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
    const [totalPages, setTotalPages] = useState(1); // Total number of pages
    const logsPerPage = 10; // Number of logs to display per page

    // Flatten the JSON directly using IDs
    const flattenData = (data, parentPath = "") => {
        const options = [];
        for (const key in data) {
            const currentPath = parentPath ? `${parentPath} > ${key}` : key;
            if (Array.isArray(data[key])) {
                data[key].forEach((item) => {
                    options.push({ label: `${currentPath} > ${item.label}`, value: item.id });
                });
            } else if (typeof data[key] === "object") {
                options.push(...flattenData(data[key], currentPath));
            }
        }
        return options;
    };

    const options = flattenData(jsonData.Expense);

    const handleChange = (selected) => {
        setSelectedOption(selected);
    };

    const fetchTransactionLogs = async (page = 1) => {
        try {
            const response = await axios.get(
                `${GLOBALVAR["SERVER_ROOT"]}/api/expense-transactions/fetch?page=${page}&limit=${logsPerPage}`
            );
            
            setTransactionLogs(response.data.logs);
            setCurrentPage(response.data.currentPage);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error("Error fetching transaction logs:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedOption) {
            setResponseMessage({ type: "error", text: "Please select an expense item." });
            return;
        }

        try {
            const payload = {
                item_id: selectedOption.value,
                label: selectedOption.label,
                amount,
                date_added: dateAdded,
                credit,
                remarks,
            };

            await axios.post(`${GLOBALVAR["SERVER_ROOT"]}/api/expense-transactions`, payload);

            setResponseMessage({ type: "success", text: "Transaction successfully added!" });

            // Reset form fields
            setSelectedOption(null);
            setAmount("");
            setDateAdded("");
            setCredit(true);
            setRemarks("");

            // Refresh logs after submission
            fetchTransactionLogs();
        } catch (error) {
            console.error("Error adding transaction:", error);
            setResponseMessage({ type: "error", text: "Failed to add transaction. Please try again." });
        }
    };

    useEffect(() => {
        // Fetch logs on initial render
        fetchTransactionLogs();
    }, []);

    const handlePageChange = (pageNumber) => {
        fetchTransactionLogs(pageNumber);
    };

    return (
        <div className="container mt-3">
            <h4>Add an Expense Transaction</h4>
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label>Select an Expense Item</label>
                    <Select
                        options={options}
                        value={selectedOption}
                        onChange={handleChange}
                        placeholder="Search or select an option..."
                        isSearchable
                    />
                </div>
                <div className="mb-3">
                    <label>Amount</label>
                    <input
                        type="number"
                        className="form-control"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter the amount"
                        required
                    />
                </div>
                <div className="mb-3">
                    <label>Date Added</label>
                    <input
                        type="date"
                        className="form-control"
                        value={dateAdded}
                        onChange={(e) => setDateAdded(e.target.value)}
                        required
                    />
                </div>
                <div className="mb-3">
                    <label>Credit/Debit</label>
                    <select
                        className="form-control"
                        value={credit}
                        onChange={(e) => setCredit(e.target.value === "true")}
                        required
                    >
                        <option value="true">Credit</option>
                        <option value="false">Debit</option>
                    </select>
                </div>
                <div className="mb-3">
                    <label>Remarks</label>
                    <textarea
                        className="form-control"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Add remarks (optional)"
                    />
                </div>
                <button type="submit" className="btn btn-primary">
                    Submit
                </button>
            </form>
            {responseMessage && (
                <div
                    className={`alert mt-3 ${responseMessage.type === "success" ? "alert-success" : "alert-danger"}`}
                >
                    {responseMessage.text}
                </div>
            )}
            <h5 className="mt-5">Transaction Logs</h5>
            {transactionLogs.length > 0 ? (
                <div>
                    <Table className="list-container" hover responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Label</th>
                                <th>Date Added</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactionLogs.map((log, index) => (
                                <tr key={log.id}>
                                    <td>{log.id}</td>
                                    <td>{log.label}</td>
                                    <td>{log.date_added}</td>
                                    <td>{log.credit ? "Credit" : "Debit"}</td>
                                    <td>
                                        <ToPeso amount={log.amount} />
                                    </td>
                                    <td>{log.remarks || "N/A"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <nav className="mt-3">
                        <ul className="pagination">
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li
                                    key={i + 1}
                                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    <button className="page-link">{i + 1}</button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
            ) : (
                <p>No transaction logs available.</p>
            )}
        </div>
    );
}


function ExpenseManagement() {
    return (
        <>
            <NavMain slug="expense-management" parent="7" />
            <HeaderMain slug="expense-management" />
            <div className="main-content financials">
                <ExpenseDropdown />
            </div>
        </>
    );
}

export default ExpenseManagement;